import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import i18n from "@/i18n";

import cls from "../MemberProfileOverView/MemberProfileOverView.module.scss";

import avatarPng from "@/assets/UI/avatar.png";
import api from "@/utils/axios";
import Loading from "@/components/Loading/Loading";

import manIcon from "@/assets/UI/man.webp";
import womanIcon from "@/assets/UI/woman.webp";
import non_binaryIcon from "@/assets/UI/non_binary.webp";
import trans_manIcon from "@/assets/UI/trans_man.webp";
import trans_womanIcon from "@/assets/UI/trans_woman.webp";
import genderfluidIcon from "@/assets/UI/genderfluid.webp";
import self_describeIcon from "@/assets/UI/self_describe.webp";

import {
  ATTRACTION_GENDERS,
  CONNECTION_TYPES,
  INTIMACY_TYPES,
  BODY_ATTRACT,
  SEXUAL_ENVIRONMENTS,
  SEXUAL_FANTASIES,
  EVENT_ATTRACT,
  BE_MATCHED,
} from "@/enums/lifestyleEnums";

/* ---------- helpers ---------- */

const GENDER_ICON_MAP = {
  man: manIcon,
  woman: womanIcon,
  non_binary: non_binaryIcon,
  trans_man: trans_manIcon,
  trans_woman: trans_womanIcon,
  genderfluid: genderfluidIcon,
  self_describe: self_describeIcon,
  not_select: self_describeIcon,
};

function getGenderIcon(gender) {
  return GENDER_ICON_MAP[gender] || self_describeIcon;
}

function asArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
}

/* ---- attraction ---- */
const ATTRACTION_ORDER = ATTRACTION_GENDERS;

function buildAttractionNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = ATTRACTION_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hlb}>
      {i18n.t(`memberProfile.attraction.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- connection types ---- */
const CONNECTION_ORDER = CONNECTION_TYPES;

function buildConnectionNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = CONNECTION_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hl}>
      {i18n.t(`memberProfile.connection.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- intimacy types ---- */
const INTIMACY_ORDER = INTIMACY_TYPES;

function buildIntimacyNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = INTIMACY_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hlb}>
      {i18n.t(`memberProfile.intimacy.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- body attract ---- */
const BODY_ATTRACT_ORDER = BODY_ATTRACT;

function buildBodyAttractNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = BODY_ATTRACT_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hlb}>
      {i18n.t(`memberProfile.bodyAttract.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- sex environment ---- */
const SEX_ENV_ORDER = SEXUAL_ENVIRONMENTS;

function buildSexEnvNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = SEX_ENV_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hl}>
      {i18n.t(`memberProfile.sexualEnvironment.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- fantasies ---- */
const SEXUAL_FANTASY_ORDER = SEXUAL_FANTASIES;

function buildSexFantasyNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = SEXUAL_FANTASY_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hlb}>
      {i18n.t(`memberProfile.sexualFantasies.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- event attract ---- */
const EVENT_ATTRACT_ORDER = EVENT_ATTRACT;

function buildEventAttractNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = EVENT_ATTRACT_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hl}>
      {i18n.t(`memberProfile.eventAttract.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ---- be matched ---- */
const BE_MATCHED_ORDER = BE_MATCHED;

function buildBeMatchedNodes(keys, css) {
  if (!keys || !keys.length) return null;
  const ordered = BE_MATCHED_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => (
    <span key={key} className={css.hlb}>
      {i18n.t(`memberProfile.beMatched.labels.${key}`)}
    </span>
  ));

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });
  return nodes;
}

/* ================== COMPONENT ================== */

const LoungeMemberAbout = () => {
  const { t } = useTranslation();
  const { memberId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);

  useEffect(() => {
    if (!memberId) return;

    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get(
          `/v1/lounge/member-profile/${memberId}/about`,
        );
        if (!ignore) {
          setMember(res.data.member || null);
        }
      } catch (err) {
        if (!ignore) {
          setError(
            err?.response?.data?.message ||
              t("memberProfile.states.error", "Something went wrong."),
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [memberId, t]);

  const personal = member?.personal || null;
  const lifestyle = member?.lifestyle || null;
  const partner = member?.primaryPartner || null;
  const partnerPersonal = partner?.personal || null;
  const partnerLifestyle = partner?.lifestyle || null;

  const memberType = member?.memberType || "single";
  const isCouple = memberType === "couple";

  const avatarSrc = member?.avatar?.url || avatarPng;
  const username = member?.username || "User";

  const rawGender = personal?.gender || "not_select";
  const rawPartnerGender = partnerPersonal?.gender || null;

  const genderIconSrc = getGenderIcon(rawGender);
  const partnerGenderIconSrc = rawPartnerGender
    ? getGenderIcon(rawPartnerGender)
    : null;

  const city = personal?.city || null;
  const country = personal?.country || null;
  const cityCountry =
    city && country ? `${city}, ${country}` : city || country || null;

  /* ---- description ---- */
  const genderPhrase = t(`memberProfile.description.gender.${rawGender}`);
  const partnerGenderPhrase = rawPartnerGender
    ? t(`memberProfile.description.gender.${rawPartnerGender}`)
    : null;

  const hasLocation = !!cityCountry;

  const descriptionKey = isCouple
    ? hasLocation
      ? "memberProfile.description.couple.withLocation"
      : "memberProfile.description.couple.noLocation"
    : hasLocation
      ? "memberProfile.description.single.withLocation"
      : "memberProfile.description.single.noLocation";

  const descriptionValues = isCouple
    ? {
        genderPhrase,
        partnerGenderPhrase,
        cityCountry: cityCountry || "",
        languageFragment: "",
        relationshipTypePhrase: "",
        relationshipStatusPhrase: "",
      }
    : {
        genderPhrase,
        cityCountry: cityCountry || "",
        languageFragment: "",
      };

  const memberLifestyle = lifestyle || null;

  /* ==== sexual identity & role ==== */
  const memberSexualIdentityKey =
    memberLifestyle?.sexual_identity || "not_select";
  const memberSexualRoleKey = memberLifestyle?.sexual_role || "not_select";

  const memberIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${memberSexualIdentityKey}`,
  );
  const memberIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${memberSexualIdentityKey}`,
  );
  const memberRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${memberSexualRoleKey}`,
  );
  const memberRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${memberSexualRoleKey}`,
  );

  const partnerSexualIdentityKey =
    partnerLifestyle?.sexual_identity || "not_select";
  const partnerSexualRoleKey = partnerLifestyle?.sexual_role || "not_select";

  const partnerIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${partnerSexualIdentityKey}`,
  );
  const partnerIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${partnerSexualIdentityKey}`,
  );
  const partnerRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${partnerSexualRoleKey}`,
  );
  const partnerRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${partnerSexualRoleKey}`,
  );

  const hasMemberSexualData =
    memberSexualIdentityKey !== "not_select" ||
    memberSexualRoleKey !== "not_select";

  const hasPartnerSexualData =
    isCouple &&
    partnerLifestyle &&
    (partnerSexualIdentityKey !== "not_select" ||
      partnerSexualRoleKey !== "not_select");

  /* ==== attraction ==== */
  const memberAttractionKeys = asArray(memberLifestyle?.attraction_genders);
  const partnerAttractionKeys = asArray(partnerLifestyle?.attraction_genders);

  const hasMemberAttraction = memberAttractionKeys.length > 0;
  const hasPartnerAttraction = partnerAttractionKeys.length > 0;

  const memberAttractionNodes = buildAttractionNodes(memberAttractionKeys, cls);
  const partnerAttractionNodes = buildAttractionNodes(
    partnerAttractionKeys,
    cls,
  );

  /* ==== connection types ==== */
  const memberConnectionKeys = asArray(memberLifestyle?.connection_types);
  const partnerConnectionKeys = asArray(partnerLifestyle?.connection_types);

  const hasMemberConnection = memberConnectionKeys.length > 0;
  const hasPartnerConnection = partnerConnectionKeys.length > 0;

  const memberConnectionNodes = buildConnectionNodes(memberConnectionKeys, cls);
  const partnerConnectionNodes = buildConnectionNodes(
    partnerConnectionKeys,
    cls,
  );

  /* ==== intimacy types ==== */
  const memberIntimacyKeys = asArray(memberLifestyle?.intimacy_types);
  const partnerIntimacyKeys = asArray(partnerLifestyle?.intimacy_types);

  const hasMemberIntimacy = memberIntimacyKeys.length > 0;
  const hasPartnerIntimacy = partnerIntimacyKeys.length > 0;

  const memberIntimacyNodes = buildIntimacyNodes(memberIntimacyKeys, cls);
  const partnerIntimacyNodes = buildIntimacyNodes(partnerIntimacyKeys, cls);

  /* ==== body type ==== */
  const memberBodyKey = memberLifestyle?.body_type || null;
  const partnerBodyKey = partnerLifestyle?.body_type || null;

  const hasMemberBodyData = !!memberBodyKey;
  const hasPartnerBodyData = !!partnerBodyKey;

  const memberBodyLabel = memberBodyKey
    ? i18n.t(`memberProfile.bodyDescription.labels.${memberBodyKey}`)
    : "";
  const memberBodyText = memberBodyKey
    ? i18n.t(`memberProfile.bodyDescription.text.${memberBodyKey}`)
    : "";

  const partnerBodyLabel = partnerBodyKey
    ? i18n.t(`memberProfile.bodyDescription.labels.${partnerBodyKey}`)
    : "";
  const partnerBodyText = partnerBodyKey
    ? i18n.t(`memberProfile.bodyDescription.text.${partnerBodyKey}`)
    : "";

  /* ==== body-attraction ==== */
  const memberBodyAttractKeys = asArray(memberLifestyle?.body_attract);
  const partnerBodyAttractKeys = asArray(partnerLifestyle?.body_attract);

  const hasMemberBodyAttract = memberBodyAttractKeys.length > 0;
  const hasPartnerBodyAttract = partnerBodyAttractKeys.length > 0;

  const memberBodyAttractNodes = buildBodyAttractNodes(
    memberBodyAttractKeys,
    cls,
  );
  const partnerBodyAttractNodes = buildBodyAttractNodes(
    partnerBodyAttractKeys,
    cls,
  );

  /* ==== sexual environment ==== */
  const memberSexEnvKeys = asArray(memberLifestyle?.sexual_environment);
  const partnerSexEnvKeys = asArray(partnerLifestyle?.sexual_environment);

  const hasMemberSexEnv = memberSexEnvKeys.length > 0;
  const hasPartnerSexEnv = partnerSexEnvKeys.length > 0;

  const memberSexEnvNodes = buildSexEnvNodes(memberSexEnvKeys, cls);
  const partnerSexEnvNodes = buildSexEnvNodes(partnerSexEnvKeys, cls);

  /* ==== fantasies ==== */
  const memberFantasyKeys = asArray(memberLifestyle?.sexual_fantasies);
  const partnerFantasyKeys = asArray(partnerLifestyle?.sexual_fantasies);

  const hasMemberFantasies = memberFantasyKeys.length > 0;
  const hasPartnerFantasies = partnerFantasyKeys.length > 0;

  const memberFantasyNodes = buildSexFantasyNodes(memberFantasyKeys, cls);
  const partnerFantasyNodes = buildSexFantasyNodes(partnerFantasyKeys, cls);

  /* ==== event attraction ==== */
  const memberEventKeys = asArray(memberLifestyle?.event_attract);
  const hasMemberEventAttract = memberEventKeys.length > 0;
  const memberEventAttractNodes = buildEventAttractNodes(memberEventKeys, cls);

  /* ==== be matched ==== */
  const memberBeMatchedKeys = asArray(memberLifestyle?.be_matched);
  const hasMemberBeMatched = memberBeMatchedKeys.length > 0;
  const memberBeMatchedNodes = buildBeMatchedNodes(memberBeMatchedKeys, cls);

  /* ==== alcohol & cannabis ==== */
  const memberAlcoholKey = memberLifestyle?.alcohol_cannabis || null;
  const partnerAlcoholKey = partnerLifestyle?.alcohol_cannabis || null;

  const hasMemberAlcoholData = !!memberAlcoholKey;
  const hasPartnerAlcoholData = !!partnerAlcoholKey;

  const memberAlcoholLabel = memberAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.labels.${memberAlcoholKey}`)
    : "";
  const memberAlcoholText = memberAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.text.${memberAlcoholKey}`)
    : "";

  const partnerAlcoholLabel = partnerAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.labels.${partnerAlcoholKey}`)
    : "";
  const partnerAlcoholText = partnerAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.text.${partnerAlcoholKey}`)
    : "";

  const isReady = !loading && !error && member;

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Header */}
        <header className={cls.header}>
          <div className={cls.headerMain}>
            <div className={cls.avatarWrap}>
              <img src={avatarSrc} alt={username} className={cls.avatar} />
              <span className={cls.badgeOnline}>
                {t("memberProfileOverview.badges.online")}
              </span>
            </div>

            <div className={cls.headerInfo}>
              <div className={cls.usernameRow}>
                <span className={cls.genderIconRow}>
                  {isCouple ? (
                    <>
                      <span className={cls.genderBadgeItem}>
                        <img
                          src={genderIconSrc}
                          alt={t(
                            "memberProfileOverview.genderIconAlt",
                            "Gender",
                          )}
                          className={cls.genderIconImage}
                        />
                      </span>
                      {partnerGenderIconSrc && (
                        <span className={cls.genderBadgeItem}>
                          <img
                            src={partnerGenderIconSrc}
                            alt={t(
                              "memberProfileOverview.partnerGenderIconAlt",
                              "Partner gender",
                            )}
                            className={cls.genderIconImage}
                          />
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={cls.genderBadgeItem}>
                      <img
                        src={genderIconSrc}
                        alt={t("memberProfileOverview.genderIconAlt", "Gender")}
                        className={cls.genderIconImage}
                      />
                    </span>
                  )}
                </span>
              </div>

              <div className={cls.metaRow}>
                {cityCountry && (
                  <span className={`${cls.metaPill} ${cls.metaPillLocation}`}>
                    {cityCountry}
                  </span>
                )}
                <span className={`${cls.metaPill} ${cls.metaPillType}`}>
                  {isCouple
                    ? t("memberProfile.member.memberType.couple")
                    : t("memberProfile.member.memberType.single")}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to={`/lounge/member-profile/${memberId}`}
            className={cls.tabButton}
          >
            {t("memberProfileOverview.tabs.timeline")}
          </Link>

          <Link
            to={`/lounge/member-profile/${memberId}/about`}
            className={`${cls.tabButton} ${cls.activeTab}`}
          >
            {t("memberProfileOverview.tabs.about")}
          </Link>
          <Link
            to={`/lounge/member-profile/${memberId}/friends`}
            className={cls.tabButton}
          >
            {t("memberProfileOverview.tabs.friends")}
          </Link>
        </nav>

        {/* Loading / error */}
        {loading && (
          <div className={cls.stateRow}>
            <span className={cls.stateDot} />
            <span className={cls.stateText}>
              {t("memberProfile.states.loading")}
            </span>
          </div>
        )}

        {!loading && error && (
          <div className={cls.stateRowError}>
            <span className={cls.stateText}>{error}</span>
          </div>
        )}

        {/* Main layout */}
        {isReady && (
          <div className={cls.layout}>
            {/* LEFT COLUMN */}
            <div className={cls.leftColumn}>
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("memberProfileOverview.about.title")}
                </h2>

                {/* Description */}
                {descriptionKey && (
                  <div className={`${cls.block} ${cls.blockDescription}`}>
                    <p className={cls.descriptionText}>
                      <Trans
                        i18nKey={descriptionKey}
                        values={descriptionValues}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                    </p>
                  </div>
                )}

                {/* Sexual identity & role */}
                <div className={`${cls.block} ${cls.blockIntimacy}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.sexualDescription.titleCouple")
                      : i18n.t("memberProfile.sexualDescription.titleSingle")}
                  </h3>

                  {!memberLifestyle || !hasMemberSexualData ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.sexualDescription.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerSexualData ? (
                    <p className={cls.sexDescription}>
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line1"
                        values={{
                          identityLabel: memberIdentityLabel,
                          identityText: memberIdentityText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                      <br />
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line2"
                        values={{
                          roleLabel: memberRoleLabel,
                          roleText: memberRoleText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                    </p>
                  ) : (
                    <>
                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine1"
                          values={{
                            identityLabel: memberIdentityLabel,
                            identityText: memberIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine2"
                          values={{
                            roleLabel: memberRoleLabel,
                            roleText: memberRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>

                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine1"
                          values={{
                            identityLabel: partnerIdentityLabel,
                            identityText: partnerIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine2"
                          values={{
                            roleLabel: partnerRoleLabel,
                            roleText: partnerRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>

                {/* Attraction genders */}
                <div className={`${cls.block} ${cls.blockAttraction}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.attraction.titleCouple")
                      : i18n.t("memberProfile.attraction.titleSingle")}
                  </h3>

                  {!hasMemberAttraction ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.attraction.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerAttraction ? (
                    <p className={cls.attrDescription}>
                      {i18n.t("memberProfile.attraction.single.prefix")}
                      {memberAttractionNodes}
                      {i18n.t("memberProfile.attraction.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.attrDescription}>
                        {i18n.t("memberProfile.attraction.couple.memberPrefix")}
                        {memberAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>

                      <p className={cls.attrDescription}>
                        {i18n.t(
                          "memberProfile.attraction.couple.partnerPrefix",
                        )}
                        {partnerAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* RIGHT COLUMN – lifestyle sections */}
            <div className={cls.rightColumn}>
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("memberProfile.about.lifestyleTitle", "Lifestyle details")}
                </h2>

                {/* Connection + intimacy */}
                <div className={cls.metaGridSecondary}>
                  <section
                    className={`${cls.metaPanel} ${cls.metaPanelConnection}`}
                  >
                    <h3 className={cls.metaPanelTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.connection.titleCouple")
                        : i18n.t("memberProfile.connection.titleSingle")}
                    </h3>

                    {!hasMemberConnection ? (
                      <p className={cls.metaPanelEmpty}>
                        {i18n.t("memberProfile.connection.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerConnection ? (
                      <p className={cls.connDescription}>
                        {i18n.t("memberProfile.connection.single.prefix")}
                        {memberConnectionNodes}
                        {i18n.t("memberProfile.connection.single.suffix")}
                      </p>
                    ) : (
                      <>
                        <p className={cls.connDescription}>
                          {i18n.t(
                            "memberProfile.connection.couple.memberPrefix",
                          )}
                          {memberConnectionNodes}
                          {i18n.t("memberProfile.connection.couple.suffix")}
                        </p>

                        <p className={cls.connDescription}>
                          {i18n.t(
                            "memberProfile.connection.couple.partnerPrefix",
                          )}
                          {partnerConnectionNodes}
                          {i18n.t("memberProfile.connection.couple.suffix")}
                        </p>
                      </>
                    )}
                  </section>

                  <section
                    className={`${cls.metaPanel} ${cls.metaPanelIntimacy}`}
                  >
                    <h3 className={cls.metaPanelTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.intimacy.titleCouple")
                        : i18n.t("memberProfile.intimacy.titleSingle")}
                    </h3>

                    {!hasMemberIntimacy ? (
                      <p className={cls.metaPanelEmpty}>
                        {i18n.t("memberProfile.intimacy.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerIntimacy ? (
                      <p className={cls.intimacyDescription}>
                        {i18n.t("memberProfile.intimacy.single.prefix")}
                        {memberIntimacyNodes}
                        {i18n.t("memberProfile.intimacy.single.suffix")}
                      </p>
                    ) : (
                      <>
                        <p className={cls.intimacyDescription}>
                          {i18n.t("memberProfile.intimacy.couple.memberPrefix")}
                          {memberIntimacyNodes}
                          {i18n.t("memberProfile.intimacy.couple.suffix")}
                        </p>

                        <p className={cls.intimacyDescription}>
                          {i18n.t(
                            "memberProfile.intimacy.couple.partnerPrefix",
                          )}
                          {partnerIntimacyNodes}
                          {i18n.t("memberProfile.intimacy.couple.suffix")}
                        </p>
                      </>
                    )}
                  </section>
                </div>

                {/* Body + body attract */}
                <div className={cls.metaGrid}>
                  <div className={`${cls.block} ${cls.blockBody}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.bodyDescription.titleCouple")
                        : i18n.t("memberProfile.bodyDescription.titleSingle")}
                    </h3>

                    {!memberLifestyle || !hasMemberBodyData ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.bodyDescription.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerBodyData ? (
                      <p className={cls.bodyDescription}>
                        <Trans
                          i18nKey="memberProfile.bodyDescription.single.line1"
                          values={{
                            bodyLabel: memberBodyLabel,
                            bodyText: memberBodyText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                          }}
                        />
                      </p>
                    ) : (
                      <>
                        <p className={cls.bodyDescription}>
                          <Trans
                            i18nKey="memberProfile.bodyDescription.couple.memberLine"
                            values={{
                              bodyLabel: memberBodyLabel,
                              bodyText: memberBodyText,
                            }}
                            components={{
                              hl: <span className={cls.hl} />,
                            }}
                          />
                        </p>

                        <p className={cls.bodyDescription}>
                          <Trans
                            i18nKey="memberProfile.bodyDescription.couple.partnerLine"
                            values={{
                              bodyLabel: partnerBodyLabel,
                              bodyText: partnerBodyText,
                            }}
                            components={{
                              hl: <span className={cls.hl} />,
                            }}
                          />
                        </p>
                      </>
                    )}
                  </div>

                  <div className={`${cls.block} ${cls.blockBodyAttract}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.bodyAttract.titleCouple")
                        : i18n.t("memberProfile.bodyAttract.titleSingle")}
                    </h3>

                    {!hasMemberBodyAttract ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.bodyAttract.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerBodyAttract ? (
                      <p className={cls.bodyAttractDescription}>
                        {i18n.t("memberProfile.bodyAttract.single.prefix")}
                        {memberBodyAttractNodes}
                        {i18n.t("memberProfile.bodyAttract.single.suffix")}
                      </p>
                    ) : (
                      <>
                        <p className={cls.bodyAttractDescription}>
                          {i18n.t(
                            "memberProfile.bodyAttract.couple.memberPrefix",
                          )}
                          {memberBodyAttractNodes}
                          {i18n.t("memberProfile.bodyAttract.couple.suffix")}
                        </p>

                        <p className={cls.bodyAttractDescription}>
                          {i18n.t(
                            "memberProfile.bodyAttract.couple.partnerPrefix",
                          )}
                          {partnerBodyAttractNodes}
                          {i18n.t("memberProfile.bodyAttract.couple.suffix")}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Sexual environment + fantasies */}
                <div className={cls.metaGrid}>
                  <div className={`${cls.block} ${cls.blockSexEnv}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.sexualEnvironment.titleCouple")
                        : i18n.t("memberProfile.sexualEnvironment.titleSingle")}
                    </h3>

                    {!hasMemberSexEnv ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.sexualEnvironment.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerSexEnv ? (
                      <p className={cls.sexEnvDescription}>
                        {i18n.t(
                          "memberProfile.sexualEnvironment.single.prefix",
                        )}
                        {memberSexEnvNodes}
                        {i18n.t(
                          "memberProfile.sexualEnvironment.single.suffix",
                        )}
                      </p>
                    ) : (
                      <>
                        <p className={cls.sexEnvDescription}>
                          {i18n.t(
                            "memberProfile.sexualEnvironment.couple.memberPrefix",
                          )}
                          {memberSexEnvNodes}
                          {i18n.t(
                            "memberProfile.sexualEnvironment.couple.suffix",
                          )}
                        </p>

                        <p className={cls.sexEnvDescription}>
                          {i18n.t(
                            "memberProfile.sexualEnvironment.couple.partnerPrefix",
                          )}
                          {partnerSexEnvNodes}
                          {i18n.t(
                            "memberProfile.sexualEnvironment.couple.suffix",
                          )}
                        </p>
                      </>
                    )}
                  </div>

                  <div className={`${cls.block} ${cls.blockFantasies}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.sexualFantasies.titleCouple")
                        : i18n.t("memberProfile.sexualFantasies.titleSingle")}
                    </h3>

                    {!hasMemberFantasies ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.sexualFantasies.empty")}
                      </p>
                    ) : !isCouple || !hasPartnerFantasies ? (
                      <p className={cls.sexFantasyDescription}>
                        {i18n.t("memberProfile.sexualFantasies.single.prefix")}
                        {memberFantasyNodes}
                        {i18n.t("memberProfile.sexualFantasies.single.suffix")}
                      </p>
                    ) : (
                      <>
                        <p className={cls.sexFantasyDescription}>
                          {i18n.t(
                            "memberProfile.sexualFantasies.couple.memberPrefix",
                          )}
                          {memberFantasyNodes}
                          {i18n.t(
                            "memberProfile.sexualFantasies.couple.suffix",
                          )}
                        </p>

                        <p className={cls.sexFantasyDescription}>
                          {i18n.t(
                            "memberProfile.sexualFantasies.couple.partnerPrefix",
                          )}
                          {partnerFantasyNodes}
                          {i18n.t(
                            "memberProfile.sexualFantasies.couple.suffix",
                          )}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Event attraction + be matched */}
                <div className={cls.metaGrid}>
                  <div className={`${cls.block} ${cls.blockEventAttract}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.eventAttract.titleCouple")
                        : i18n.t("memberProfile.eventAttract.titleSingle")}
                    </h3>

                    {!hasMemberEventAttract ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.eventAttract.empty")}
                      </p>
                    ) : (
                      <p className={cls.eventAttractDescription}>
                        {isCouple
                          ? i18n.t("memberProfile.eventAttract.couple.prefix")
                          : i18n.t("memberProfile.eventAttract.single.prefix")}
                        {memberEventAttractNodes}
                        {isCouple
                          ? i18n.t("memberProfile.eventAttract.couple.suffix")
                          : i18n.t("memberProfile.eventAttract.single.suffix")}
                      </p>
                    )}
                  </div>

                  <div className={`${cls.block} ${cls.blockBeMatched}`}>
                    <h3 className={cls.blockTitle}>
                      {isCouple
                        ? i18n.t("memberProfile.beMatched.titleCouple")
                        : i18n.t("memberProfile.beMatched.titleSingle")}
                    </h3>

                    {!hasMemberBeMatched ? (
                      <p className={cls.emptyText}>
                        {i18n.t("memberProfile.beMatched.empty")}
                      </p>
                    ) : (
                      <p className={cls.beMatchedDescription}>
                        {isCouple
                          ? i18n.t("memberProfile.beMatched.couple.prefix")
                          : i18n.t("memberProfile.beMatched.single.prefix")}
                        {memberBeMatchedNodes}
                        {isCouple
                          ? i18n.t("memberProfile.beMatched.couple.suffix")
                          : i18n.t("memberProfile.beMatched.single.suffix")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Alcohol & cannabis */}
                <div className={`${cls.block} ${cls.blockAlcohol}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.alcoholCannabis.titleCouple")
                      : i18n.t("memberProfile.alcoholCannabis.titleSingle")}
                  </h3>

                  {!memberLifestyle || !hasMemberAlcoholData ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.alcoholCannabis.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerAlcoholData ? (
                    <p className={cls.alcoholText}>
                      <Trans
                        i18nKey="memberProfile.alcoholCannabis.single.line1"
                        values={{
                          label: memberAlcoholLabel,
                          text: memberAlcoholText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                        }}
                      />
                    </p>
                  ) : (
                    <>
                      <p className={cls.alcoholText}>
                        <Trans
                          i18nKey="memberProfile.alcoholCannabis.couple.memberLine"
                          values={{
                            label: memberAlcoholLabel,
                            text: memberAlcoholText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                          }}
                        />
                      </p>

                      <p className={cls.alcoholText}>
                        <Trans
                          i18nKey="memberProfile.alcoholCannabis.couple.partnerLine"
                          values={{
                            label: partnerAlcoholLabel,
                            text: partnerAlcoholText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoungeMemberAbout;
